<template>
    <div class="portal-page">
        <!-- 顶部品牌区：携程 logo + 文字 -->
        <div class="nav-bar">
            <div class="nav-brand">
                <img class="nav-logo" src="/static/logo.png" alt="携程logo" />
                <span class="nav-title">携程</span>
            </div>
            <span class="nav-subtitle">心理健康关爱服务 · 专业 · 安全 · 保密</span>
        </div>

        <!-- AI心理陪伴登录卡：插画 + 手机号登录，登录后进入模式选择页 -->
        <div class="login-card">
            <div class="card-pic">
                <img class="card-img" src="/static/images/portal-banner.png" alt="AI心理陪伴" />
            </div>
            <div class="card-body">
                <div class="card-title">AI心理陪伴</div>
                <div class="card-desc">智能对话式心理疏导</div>
                <!-- 已登录无需再输入手机号 -->
                <div class="login-input-wrapper" v-if="!isLoggedIn">
                    <span class="login-input-prefix">+86</span>
                    <input
                        class="login-phone-input"
                        type="number"
                        v-model="loginPhone"
                        placeholder="请输入手机号"
                        maxlength="11"
                    />
                </div>
                <div
                    class="login-submit-btn"
                    :class="{ disabled: enterDisabled }"
                    @click="handleEnter"
                >
                    <span>{{ enterBtnText }}</span>
                </div>
            </div>
        </div>

        <!-- 底部声明 -->
        <div class="footer-bar">
            <span class="footer-line"></span>
        </div>
    </div>
</template>

<script>
import { request } from '@/utils/request.js';
import { getStorage, setStorage } from '@/utils/storage.js';

export default {
    data() {
        return {
            loginPhone: '', // 登录输入的手机号
            isLogining: false, // 是否正在登录
            isLoggedIn: false, // 是否已登录（token 有效）
        };
    },
    computed: {
        // 手机号格式是否合法
        canLogin() {
            return /^1[3-9]\d{9}$/.test(this.loginPhone.trim());
        },
        // 登录按钮是否置灰（已登录始终可点）
        enterDisabled() {
            if (this.isLoggedIn) return false;
            return !this.canLogin || this.isLogining;
        },
        // 按钮文案：已登录显示进入聊天
        enterBtnText() {
            if (this.isLoggedIn) return '进入聊天';
            return this.isLogining ? '验证中...' : '登录';
        },
    },
    created() {
        // 进入页面先校验本地 token，有效则免登录直达
        this.checkLogin();
    },
    methods: {
        /**
         * 校验本地 token 是否有效
         * 有效则记录用户信息并直接进入模式选择页（对齐参考项目交互）
         */
        async checkLogin() {
            const token = getStorage('token');
            if (!token) return;
            try {
                const res = await request({
                    url: '/api/verify-token',
                    method: 'GET',
                });
                if (res.data.code === 200) {
                    const userData = res.data.data;
                    setStorage('userPhone', userData.phoneNumber);
                    setStorage('userId', userData.userId);
                    setStorage('hasCompletedAssessment', userData.hasCompletedAssessment);
                    this.isLoggedIn = true;
                    // token 有效，进入模式选择页由用户选择模式
                    this.$router.replace('/choice');
                }
            } catch (e) {
                // token 验证失败，保持未登录状态
            }
        },

        /** 主按钮点击：已登录直接进模式选择页，未登录走手机号登录 */
        handleEnter() {
            if (this.enterDisabled) return;
            if (this.isLoggedIn) {
                this.$router.replace('/choice');
                return;
            }
            this.handleLogin();
        },

        /** 手机号登录，成功后进入模式选择页 */
        async handleLogin() {
            if (this.isLogining) return;
            this.isLogining = true;
            try {
                const res = await request({
                    url: '/api/phone-login',
                    method: 'POST',
                    data: { phoneNumber: this.loginPhone.trim() },
                    needAuth: false,
                });
                if (res.data.code === 200) {
                    const userData = res.data.data;
                    setStorage('token', userData.token);
                    setStorage('userPhone', userData.phoneNumber);
                    setStorage('userId', userData.userId);
                    setStorage('hasCompletedAssessment', userData.hasCompletedAssessment);
                    // 登录成功，进入模式选择页
                    this.$router.replace('/choice');
                } else {
                    this.$toast(res.data.message || '登录失败');
                }
            } catch (e) {
                this.$toast('网络错误，请重试');
            } finally {
                this.isLogining = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
/* ==================== 页面基础 ==================== */
.portal-page {
    width: 100vw;
    min-height: 100vh;
    // 携程蓝：顶部浅蓝渐变过渡到浅灰
    background: linear-gradient(180deg, #d8ebff 0%, #e9f4ff 24%, #f5f7fa 60%);
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', sans-serif;
}

/* ==================== 顶部品牌区 ==================== */
.nav-bar {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36rpx 0 8rpx;
}

// logo + 品牌名横排
.nav-brand {
    display: flex;
    align-items: center;
}

.nav-logo {
    width: 64rpx;
    height: 64rpx;
    margin-right: 12rpx; // 与文字间距（替代 gap）
    display: block;
    object-fit: contain; // 保持 logo 比例不变形
}

.nav-title {
    font-size: 40rpx;
    font-weight: 700;
    color: #0a6cf5; // 携程品牌蓝
    letter-spacing: 2rpx;
}

.nav-subtitle {
    margin-top: 12rpx;
    font-size: 22rpx;
    color: #7ba4d6;
    letter-spacing: 4rpx; // 副标题字间距，营造标语感
}

/* ==================== AI心理陪伴登录卡 ==================== */
.login-card {
    margin: 32rpx 32rpx 0;
    background: #ffffff;
    border-radius: 24rpx;
    overflow: hidden;
    box-shadow: 0 8rpx 24rpx rgba(10, 108, 245, 0.15);
}

// 顶部插画区
.card-pic {
    position: relative;
    height: 360rpx;
}

.card-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover; // 覆盖整个插画区域
    display: block;
}

// 下方文字与登录表单区
.card-body {
    padding: 32rpx 36rpx 44rpx;
}

.card-title {
    font-size: 44rpx;
    font-weight: 700;
    color: #123c7a;
}

.card-desc {
    margin-top: 12rpx;
    font-size: 24rpx;
    color: #8a94a6;
}

/* 手机号输入框（沿用原登录弹窗样式） */
.login-input-wrapper {
    display: flex;
    align-items: center;
    height: 92rpx;
    background: #f5f6fa;
    border-radius: 14rpx;
    border: 2rpx solid #ebeef5;
    margin-top: 36rpx; // 与副标题间距（按钮与上方间距统一由 .login-submit-btn 的 margin-top 控制）
    overflow: hidden;
}

.login-input-prefix {
    padding: 0 20rpx 0 24rpx;
    font-size: 28rpx;
    color: #8a94a6;
    border-right: 2rpx solid #ebeef5;
    margin-right: 12rpx;
    line-height: 92rpx;
}

.login-phone-input {
    flex: 1;
    height: 92rpx;
    font-size: 30rpx;
    color: #1a2332;
    padding-right: 24rpx;
    border: none;
    outline: none;
    background: transparent;
}

/* 登录/进入聊天按钮（携程蓝渐变主按钮） */
.login-submit-btn {
    width: 100%;
    height: 88rpx;
    margin-top: 32rpx; // 与上方文字/输入框的间距（已登录无输入框时同样生效）
    background: linear-gradient(135deg, #5db1ff 0%, #0a6cf5 100%);
    border-radius: 14rpx;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8rpx 24rpx rgba(10, 108, 245, 0.3);
    transition: opacity 0.2s ease;

    span {
        font-size: 30rpx;
        color: #ffffff;
        font-weight: 600;
    }

    &.disabled {
        opacity: 0.5;
    }

    &:active {
        transform: scale(0.98);
    }
}

/* ==================== 底部 ==================== */
.footer-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20rpx 0;
}

.footer-line {
    display: inline-block;
    width: 40rpx;
    height: 1rpx;
    background: #d0d6e0;
}
</style>
